import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, Stat, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
  Legend,
} from "recharts";
import { ArrowUp, ArrowDown, Warning, Trophy, ShieldCheck, ArrowRight } from "@phosphor-icons/react";

export default function DashboardPage() {
  const { activeCompany } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [glance, setGlance] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [salesmen, setSalesmen] = useState(null);
  const [vat, setVat] = useState(null);

  useEffect(() => {
    if (!activeCompany) return;
    setData(null);
    setGlance(null);
    setRecovery(null);
    setSalesmen(null);
    setVat(null);
    Promise.all([
      api.get(`/companies/${activeCompany.id}/dashboard`),
      api.get(`/companies/${activeCompany.id}/dashboard/glance`),
      api.get(`/companies/${activeCompany.id}/dashboard/recovery-score`),
    ])
      .then(([d, g, r]) => {
        setData(d.data);
        setGlance(g.data);
        setRecovery(r.data);
      })
      .catch(() => {});
    // VAT is optional — only load if company has a VAT number
    if (activeCompany.vat_number) {
      api.get(`/companies/${activeCompany.id}/mtd/dashboard`)
        .then((res) => setVat(res.data))
        .catch(() => setVat(null));
    }
  }, [activeCompany]);

  if (!activeCompany) {
    return (
      <PageBody>
        <div className="overline">Select a company from the header to begin.</div>
      </PageBody>
    );
  }

  const t = data?.totals || {};
  const cur = activeCompany.currency;
  const chartData = [
    { name: "Assets", value: t.asset || 0 },
    { name: "Liabilities", value: t.liability || 0 },
    { name: "Equity", value: t.equity || 0 },
    { name: "Revenue", value: t.revenue || 0 },
    { name: "Expense", value: t.expense || 0 },
  ];

  return (
    <>
      <PageHeader
        eyebrow={`Company · ${activeCompany.name}`}
        title="Consolidated Overview"
        description="Live figures across your general ledger. All numbers reflect posted journal entries."
      />
      <PageBody>
        {/* Ledger totals row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          <Stat label="Total Assets" value={fmtMoney(t.asset, cur)} testId="stat-assets" />
          <Stat label="Total Liabilities" value={fmtMoney(t.liability, cur)} testId="stat-liabilities" />
          <Stat label="Revenue (YTD)" value={fmtMoney(t.revenue, cur)} testId="stat-revenue" />
          <Stat label="Net Income" value={fmtMoney(data?.net_income, cur)} testId="stat-net-income" />
        </div>

        {/* VAT card — only when company has VAT number */}
        {vat?.eligible && (
          <VATCard vat={vat} currency={cur} onPrepare={() => navigate("/mtd")} />
        )}

        {/* Business at a glance */}
        <section>
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="overline">Business at a Glance</div>
              <h2 className="font-serif-display text-2xl mt-1">This week & this month</h2>
            </div>
            {glance?.as_of && (
              <div className="overline">As of {glance.as_of}</div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            <GlanceCard
              title="This Week"
              period={glance?.week?.start ? `Since ${glance.week.start}` : ""}
              income={glance?.week?.income}
              expense={glance?.week?.expense}
              net={glance?.week?.net}
              currency={cur}
              testId="glance-week"
            />
            <GlanceCard
              title="This Month"
              period={glance?.month?.start ? `Since ${glance.month.start}` : ""}
              income={glance?.month?.income}
              expense={glance?.month?.expense}
              net={glance?.month?.net}
              currency={cur}
              testId="glance-month"
            />
          </div>
        </section>

        {/* Cash flow 30-day chart */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border border border-border">
          <div className="lg:col-span-2 bg-card p-6" data-testid="cash-flow-card">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="overline">30-Day Cash Flow</div>
                <h2 className="font-serif-display text-2xl mt-1">Inflow vs Outflow</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Movement across all cash & bank accounts
                </p>
              </div>
              {glance?.cash_flow_30d && (
                <NetTotal series={glance.cash_flow_30d} currency={cur} />
              )}
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={glance?.cash_flow_30d || []}
                  margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="inFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="outFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" tickFormatter={(d) => d.slice(5)} interval={4} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                    formatter={(v, key) => [fmtMoney(v, cur), key]}
                  />
                  <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }} />
                  <Area type="monotone" dataKey="inflow" stroke="hsl(var(--primary))" strokeWidth={1.5} fill="url(#inFill)" name="Inflow" />
                  <Area type="monotone" dataKey="outflow" stroke="hsl(var(--destructive))" strokeWidth={1.5} fill="url(#outFill)" name="Outflow" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <RecoveryCard recovery={recovery} currency={cur} />
        </section>

        {/* Receivables + Composition */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border border border-border">
          <div className="lg:col-span-2 bg-card p-6">
            <div className="overline mb-4">Ledger Composition</div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 2,
                      fontFamily: "IBM Plex Mono",
                      fontSize: 12,
                    }}
                    formatter={(v) => fmtMoney(v, cur)}
                  />
                  <Bar dataKey="value" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-card p-6 space-y-4">
            <div className="overline">Working Queue</div>
            <QueueRow label="Bank Txns Unmatched" value={data?.counts?.bank_unmatched ?? "—"} />
            <QueueRow label="AR Invoices" value={data?.counts?.invoices_ar ?? "—"} />
            <QueueRow label="AP Bills" value={data?.counts?.invoices_ap ?? "—"} />
            <QueueRow label="Total Journals" value={data?.counts?.journals ?? "—"} />
            {glance?.receivables && (
              <div className="pt-3 border-t border-border space-y-2 mt-2">
                <div className="overline">Receivables</div>
                <QueueRow
                  label="Outstanding"
                  value={fmtMoney(glance.receivables.outstanding, cur)}
                />
                {glance.receivables.overdue > 0 && (
                  <div
                    className="flex items-center justify-between text-destructive"
                    data-testid="glance-overdue"
                  >
                    <span className="text-sm flex items-center gap-1">
                      <Warning size={14} /> Overdue
                    </span>
                    <span className="font-serif-display text-lg num">
                      {fmtMoney(glance.receivables.overdue, cur)}
                    </span>
                  </div>
                )}
              </div>
            )}
            {salesmen?.rows?.length > 0 && (
              <div className="pt-3 border-t border-border space-y-2 mt-2" data-testid="dash-salesmen">
                <div className="overline">Top Salesmen (This Month)</div>
                {salesmen.rows.filter((r) => r.salesman_id).slice(0, 4).map((r) => (
                  <div key={r.salesman_id} className="flex items-center justify-between text-sm">
                    <span className="font-mono truncate">{r.name}</span>
                    <span className="num text-right">
                      {fmtMoney(r.sales, cur)}
                      <span className="text-[10px] text-muted-foreground ml-1">
                        · {fmtMoney(r.commission, cur)} comm
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </PageBody>
    </>
  );
}

function GlanceCard({ title, period, income, expense, net, currency, testId }) {
  const isPositive = (net ?? 0) >= 0;
  return (
    <div className="bg-card p-6 space-y-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">{title}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            {period}
          </div>
        </div>
        <div
          className={`tag-badge ${isPositive ? "text-primary" : "text-destructive"}`}
        >
          {isPositive ? "SURPLUS" : "DEFICIT"}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <ArrowUp size={10} weight="bold" /> Income
          </div>
          <div className="font-serif-display text-2xl mt-1 num">
            {fmtMoney(income, currency)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <ArrowDown size={10} weight="bold" /> Expense
          </div>
          <div className="font-serif-display text-2xl mt-1 num">
            {fmtMoney(expense, currency)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Net
          </div>
          <div
            className={`font-serif-display text-2xl mt-1 num ${
              isPositive ? "text-primary" : "text-destructive"
            }`}
          >
            {fmtMoney(net, currency)}
          </div>
        </div>
      </div>
    </div>
  );
}

function RecoveryCard({ recovery, currency }) {
  const overall = recovery?.overall_score;
  const tone =
    overall == null
      ? "text-muted-foreground"
      : overall >= 80
      ? "text-primary"
      : overall >= 50
      ? "text-accent-foreground"
      : "text-destructive";
  return (
    <div className="bg-card p-6 space-y-4" data-testid="recovery-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">30-Day Recovery Score</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            AR collected ÷ (collected + still open)
          </div>
        </div>
        <Trophy size={18} className={tone} weight="regular" />
      </div>
      <div className={`font-serif-display text-6xl leading-none tracking-tight num ${tone}`}>
        {overall == null ? "—" : `${overall}%`}
      </div>
      {recovery && (
        <>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Collected</span>
            <span className="num">{fmtMoney(recovery.total_paid, currency)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Still open</span>
            <span className="num">{fmtMoney(recovery.total_open, currency)}</span>
          </div>
          <div className="pt-3 border-t border-border space-y-2">
            <div className="overline">By Method</div>
            {["stripe", "paystack", "manual"].map((k) => {
              const b = recovery.by_method[k];
              return (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="capitalize font-mono">{k}</span>
                  <span className={`num ${b.score >= 80 ? "text-primary" : b.score < 50 && b.score != null ? "text-destructive" : ""}`}>
                    {b.score == null ? "—" : `${b.score}%`}
                    <span className="text-muted-foreground ml-2 text-[10px]">
                      ({b.count_paid}/{b.count_paid + b.count_open})
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function NetTotal({ series, currency }) {
  const totalIn = series.reduce((s, r) => s + (r.inflow || 0), 0);
  const totalOut = series.reduce((s, r) => s + (r.outflow || 0), 0);
  const net = totalIn - totalOut;
  return (
    <div className="text-right">
      <div className="overline">Net 30D</div>
      <div
        className={`font-serif-display text-2xl num ${
          net >= 0 ? "text-primary" : "text-destructive"
        }`}
        data-testid="cash-flow-net"
      >
        {fmtMoney(net, currency)}
      </div>
    </div>
  );
}

function QueueRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border pb-3 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-serif-display text-2xl num">{value}</span>
    </div>
  );
}

function VATCard({ vat, currency, onPrepare }) {
  const b = vat.boxes || {};
  const daysUntil = vat.days_until_due;
  const netVat = b.netVatDue || 0;
  const owed = (b.totalVatDue || 0) >= (b.vatReclaimedCurrPeriod || 0);
  const urgent = daysUntil != null && daysUntil <= 14;
  const tone = urgent ? "text-destructive" : "text-primary";

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-4 gap-px bg-border border border-border"
      data-testid="dash-vat-card"
    >
      {/* Left panel — headline */}
      <div className="lg:col-span-2 bg-card p-6 flex flex-col justify-between">
        <div>
          <div className="overline flex items-center gap-2">
            <ShieldCheck size={12} />
            HMRC VAT — Current Return
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            {vat.period?.from_date} → {vat.period?.to_date}
            {vat.source === "hmrc" && (
              <span className="ml-2 tag-badge text-primary">HMRC obligation</span>
            )}
            {!vat.connected && (
              <span className="ml-2 tag-badge text-muted-foreground">Not connected</span>
            )}
          </div>
          <div className="mt-4">
            <div className="overline text-muted-foreground">
              {owed ? "Estimated Amount Due (Box 5)" : "Estimated Reclaim (Box 5)"}
            </div>
            <div className={`font-serif-display text-5xl leading-none tracking-tight num mt-1 ${tone}`}>
              {fmtMoney(netVat, currency)}
            </div>
          </div>
        </div>
        <Button
          onClick={onPrepare}
          data-testid="dash-vat-prepare-btn"
          className="rounded-sm h-10 mt-6 font-mono uppercase tracking-wider text-xs self-start"
        >
          Prepare Return <ArrowRight size={14} className="ml-2" />
        </Button>
      </div>

      {/* Middle — Box breakdown */}
      <div className="bg-card p-6 space-y-3">
        <div className="overline">This Quarter</div>
        <VATRow label="Box 1 · VAT on sales" value={b.vatDueSales} currency={currency} />
        <VATRow label="Box 4 · VAT reclaimed" value={b.vatReclaimedCurrPeriod} currency={currency} />
        <VATRow label="Box 3 · Total VAT due" value={b.totalVatDue} currency={currency} bold />
        <div className="pt-2 border-t border-border">
          <VATRow label="Box 6 · Net sales" value={b.totalValueSalesExVAT} currency={currency} intOnly />
          <VATRow label="Box 7 · Net purchases" value={b.totalValuePurchasesExVAT} currency={currency} intOnly />
        </div>
      </div>

      {/* Right — Due countdown */}
      <div className="bg-card p-6 flex flex-col justify-between">
        <div>
          <div className="overline">Return Due</div>
          <div className={`font-serif-display text-3xl mt-1 num ${tone}`}>
            {vat.due_date || "—"}
          </div>
        </div>
        <div>
          <div className="overline">Days Remaining</div>
          <div className={`font-serif-display text-6xl leading-none tracking-tight num mt-1 ${tone}`}>
            {daysUntil == null ? "—" : daysUntil}
          </div>
          {urgent && daysUntil >= 0 && (
            <div className="mt-2 text-xs text-destructive flex items-center gap-1">
              <Warning size={12} weight="bold" /> File soon to avoid HMRC penalties
            </div>
          )}
          {daysUntil != null && daysUntil < 0 && (
            <div className="mt-2 text-xs text-destructive flex items-center gap-1">
              <Warning size={12} weight="fill" /> {Math.abs(daysUntil)} days overdue
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VATRow({ label, value, currency, bold, intOnly }) {
  const formatted = intOnly
    ? (value ?? 0).toLocaleString()
    : fmtMoney(value ?? 0, currency);
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={`${bold ? "font-semibold" : "text-muted-foreground"}`}>{label}</span>
      <span className={`num ${bold ? "font-semibold" : ""}`}>{formatted}</span>
    </div>
  );
}

